import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

// Remove the import for the deleted GlobalReachChart

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [topKols, setTopKols] = useState<any[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const { toast } = useToast();
  
  const userAccess = localStorage.getItem('dashboard_user_access');
  const userId = localStorage.getItem('dashboard_user_id');
  const userName = localStorage.getItem('dashboard_user_name');
  const hasFullAccess = userAccess === 'full-access';

  const fetchTopKols = async () => {
    try {
      setIsLoading(true);
      
      let query = supabase.from('kol_tracker').select('*').order('content_views', { ascending: false }).limit(5);
      
      // Filter data if user has limited access
      if (!hasFullAccess && userName) {
        query = query.eq('pic', userName);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      setTopKols(data || []);
    } catch (err) {
      console.error("Error fetching top KOLs:", err);
      toast({
        title: "Error",
        description: "Failed to fetch top KOLs",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setIsLoading(true);
      
      let query = supabase.from('kol_tracker').select('*').order('created_at', { ascending: false }).limit(5);
      
      // Filter data if user has limited access
      if (!hasFullAccess && userName) {
        query = query.eq('pic', userName);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }
      
      setRecentActivities(data || []);
    } catch (err) {
      console.error("Error fetching recent activities:", err);
      toast({
        title: "Error",
        description: "Failed to fetch recent activities",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTopKols();
    fetchRecentActivities();
  }, [hasFullAccess, userName, toast]);
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-dashboard-text-primary">Dashboard</h1>
        {!hasFullAccess && (
          <div className="text-sm text-[#8E9196] italic">
            Limited access mode: You can only view data for your own KOL records
          </div>
        )}
        <Button>
          <PlusIcon className="mr-2 h-4 w-4" /> Add Record
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="dashboard-card bg-transparent border-[#4A4A4A]">
          <CardHeader className="border-b border-[#4A4A4A] pb-3">
            <CardTitle>Top KOLs</CardTitle>
            <CardDescription>KOLs with the most content views</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {isLoading ? (
              <p>Loading top KOLs...</p>
            ) : (
              <div className="space-y-4">
                {topKols.map((kol, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>{kol.kol_name}</div>
                    <div className="font-medium">{new Intl.NumberFormat().format(kol.content_views || 0)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="dashboard-card bg-transparent border-[#4A4A4A]">
          <CardHeader className="border-b border-[#4A4A4A] pb-3">
            <CardTitle>Recent Activities</CardTitle>
            <CardDescription>Latest KOL activities and updates</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {isLoading ? (
              <p>Loading recent activities...</p>
            ) : (
              <div className="space-y-4">
                {recentActivities.map((activity, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>{activity.kol_name}</div>
                    <div className="text-sm text-[#8E9196]">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="dashboard-card bg-transparent border-[#4A4A4A]">
          <CardHeader className="border-b border-[#4A4A4A] pb-3">
            <CardTitle>Total Reach</CardTitle>
            <CardDescription>Cumulative audience reach</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-4xl font-bold">
              {isLoading ? "..." : new Intl.NumberFormat().format(
                topKols.reduce((sum, item) => sum + (item.content_views || 0), 0)
              )}
            </div>
            <p className="text-[#8E9196] mt-2">Total content views</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
